import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import { ConfirmModal, StateView } from '@/card/record'
import {
  declareCustomField,
  fetchCustomFields,
  hideCustomField,
  type CustomField,
  type CustomFieldType,
} from '@/customfields/api'
import { NAVIGATION } from '@/app/navigation'
import { request } from '@/lib/api'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  RowMenu,
  SkeletonTable,
  Switch,
  isForbidden,
  useAdminEditing,
} from './AdminShell'

/**
 * Пользовательские поля сущностей (тикет #106, макет AdmFields.dc.html).
 *
 * Поле, объявленное здесь, сразу появляется на карточках сущности, в колонках и фильтрах
 * её списка — без релиза. Удаления нет, есть скрытие: значения остаются в записях.
 * Вернуть скрытое поле с экрана нельзя (в API нет), поэтому скрытие спрашивает
 * подтверждение.
 *
 * Код поля становится ключом в базе и именем колонки списка, поэтому вводится отдельно от
 * подписи и не меняется: переименовать подпись можно, а код — значит потерять значения.
 * Заполненности поля по записям API не отдаёт — колонки «Заполнено у» на экране нет.
 */

const TYPES: CustomFieldType[] = ['string', 'number', 'date', 'boolean', 'enum']
const TYPE_ICON: Record<CustomFieldType, IconName> = {
  string: 'adm-text',
  number: 'adm-hash',
  date: 'adm-date',
  boolean: 'adm-bool',
  enum: 'lines',
  reference: 'link',
  file: 'file',
}
/** The server's rule, repeated only to say it under the field: it re-checks on save. */
const CODE = /^[a-z][a-z0-9_]{0,62}$/

/** The menu item that shows the entity's list names it; the code is the fallback. */
function entityTitle(t: (key: string) => string, entity: string): string {
  for (const section of NAVIGATION) {
    const item = section.items.find((candidate) => candidate.listEntity === entity)
    if (item) return t(item.labelKey)
  }
  return entity
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

/** «Зона доставки» → `zona_dostavki`: a suggestion, not a rule — the administrator decides. */
export function suggestCode(label: string): string {
  const latin = [...label.trim().toLowerCase()].map((ch) => TRANSLIT[ch] ?? ch).join('')
  const code = latin.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/^[0-9_]+/, '')
  return code.slice(0, 63)
}

const fetchEntities = (signal?: AbortSignal) =>
  request<{ entities: string[] }>('/lists', { signal }).then((r) => r.entities)

export function CustomFieldsPage() {
  const { t } = useApp()
  const editing = useAdminEditing()
  const queryClient = useQueryClient()
  const entities = useQuery({ queryKey: ['list-catalog'], queryFn: ({ signal }) => fetchEntities(signal) })
  const [entity, setEntity] = useState('sales.deal')
  const fields = useQuery({
    queryKey: ['custom-fields', entity],
    queryFn: ({ signal }) => fetchCustomFields(entity, signal).then((r) => r.fields),
  })
  const [declaring, setDeclaring] = useState(false)
  const [hiding, setHiding] = useState<CustomField | null>(null)

  const refresh = () => {
    editing.clear()
    void queryClient.invalidateQueries({ queryKey: ['custom-fields', entity] })
    // The list describes custom columns too: its cached descriptor must not hide the new one.
    void queryClient.invalidateQueries({ queryKey: ['list-columns', entity] })
  }
  const hide = useMutation({
    mutationFn: (id: string) => hideCustomField(entity, id),
    onSuccess: () => {
      setHiding(null)
      refresh()
    },
    onError: (error) => {
      setHiding(null)
      editing.fail(error)
    },
  })
  const list = [...(fields.data ?? [])].sort((a, b) => a.displayOrder - b.displayOrder || a.label.localeCompare(b.label))
  const forbidden = isForbidden(fields.error)
  const title = entityTitle(t, entity)

  return (
    <AdminPage
      titleKey="adm.fields.title"
      leadKey="adm.fields.lead"
      readOnly={!editing.mayEdit}
      roKey="adm.fields.ro"
      forbidden={forbidden}
      primary={editing.mayEdit && <PrimaryButton label={t('customFields.add')} onClick={() => setDeclaring(true)} />}
    >
      <Notice text={editing.notice} />
      {!forbidden && (
        <div className="toolbar">
          <span className="t-sm muted">{t('customFields.entity')}</span>
          <select className="input input-sm" style={{ width: 240 }} aria-label={t('customFields.entity')} value={entity}
                  onChange={(e) => setEntity(e.target.value)}>
            {(entities.data ?? [entity]).map((name) => (
              <option key={name} value={name}>{entityTitle(t, name)}</option>
            ))}
          </select>
          {fields.data && <span className="t-sm muted">{t('adm.fields.count', { count: list.length })}</span>}
        </div>
      )}
      <AdminState
        isLoading={fields.isLoading}
        error={fields.error}
        onRetry={() => void fields.refetch()}
        forbiddenTitleKey="adm.fields.forbidden"
        errorTitleKey="adm.fields.error"
        skeleton={<SkeletonTable columns={5} rows={5} />}
      >
        {list.length === 0 ? (
          <StateView boxed kind="empty" title={t('adm.fields.empty', { entity: title })} description={t('adm.fields.empty.text')}
                     actions={editing.mayEdit && (
                       <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDeclaring(true)}>{t('customFields.add')}</button>
                     )} />
        ) : (
          <>
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('nav.adminCustomFields')}>
                <thead>
                  <tr>
                    <th>{t('customFields.code')}</th>
                    <th>{t('customFields.label')}</th>
                    <th>{t('customFields.type')}</th>
                    <th>{t('adm.fields.options')}</th>
                    <th>{t('customFields.required')}</th>
                    {editing.mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {list.map((field) => (
                    <tr key={field.id}>
                      <td><span className="mono">{field.code}</span></td>
                      <td>{field.label}</td>
                      <td><span className="badge"><Icon name={TYPE_ICON[field.dataType]} />{t(`customFields.type.${field.dataType}`)}</span></td>
                      <td className="wrap">
                        {field.dataType === 'enum' && field.options.length > 0
                          ? field.options.join(', ')
                          : <span className="muted">—</span>}
                      </td>
                      <td>
                        {field.required
                          ? <span className="badge b-accent">{t('adm.fields.requiredBadge')}</span>
                          : <span className="muted">—</span>}
                      </td>
                      {editing.mayEdit && (
                        <td className="adm-act">
                          <RowMenu label={t('adm.fields.actions', { code: field.code })} items={[
                            { id: 'hide', label: t('customFields.hide'), icon: 'adm-eye', danger: true, onSelect: () => setHiding(field) },
                          ]} />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="t-sm muted">{t('adm.fields.note')}</p>
          </>
        )}
      </AdminState>
      {declaring && (
        <FieldModal entity={entity} entityName={title} existing={list} nextOrder={(list.at(-1)?.displayOrder ?? 0) + 10}
                    onClose={() => setDeclaring(false)} onSaved={() => {
                      setDeclaring(false)
                      refresh()
                    }} />
      )}
      <ConfirmModal
        open={hiding != null}
        icon="adm-eye"
        title={t('adm.fields.hideConfirm', { label: hiding?.label ?? '' })}
        confirmLabel={t('customFields.hide')}
        pending={hide.isPending}
        onCancel={() => setHiding(null)}
        onConfirm={() => hiding && hide.mutate(hiding.id)}
      >
        <p>{t('adm.fields.hideConfirm.text')}</p>
      </ConfirmModal>
    </AdminPage>
  )
}

function FieldModal({ entity, entityName, existing, nextOrder, onClose, onSaved }: {
  entity: string
  entityName: string
  existing: CustomField[]
  nextOrder: number
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const typeId = useId()
  const [label, setLabel] = useState('')
  const [code, setCode] = useState('')
  const [dataType, setDataType] = useState<CustomFieldType>('string')
  const [options, setOptions] = useState<string[]>([''])
  const [required, setRequired] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const normalized = code.trim().toLowerCase()
  const taken = existing.some((field) => field.code === normalized)
  const codeError = code.trim() === ''
    ? null
    : !CODE.test(normalized)
      ? t('adm.fields.codeInvalid')
      : taken ? t('adm.fields.codeTaken', { code: normalized, entity: entityName }) : null
  const suggestion = suggestCode(label)
  const cleanOptions = options.map((option) => option.trim()).filter(Boolean)
  const incomplete = label.trim() === '' || normalized === '' || Boolean(codeError) || (dataType === 'enum' && cleanOptions.length === 0)

  const declare = useMutation({
    mutationFn: () =>
      declareCustomField(entity, {
        code: normalized,
        label: label.trim(),
        dataType,
        options: cleanOptions,
        required,
        displayOrder: nextOrder,
      }),
    onSuccess: onSaved,
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="modal"
      title={t('adm.fields.newTitle', { entity: entityName })}
      subtitle={t('adm.fields.newSub')}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={t('adm.fields.create')} pending={declare.isPending}
                            disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 16 }} aria-label={t('customFields.declare')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        declare.mutate()
      }}>
        <Field label={t('customFields.label')} htmlFor="cf-label" required>
          <input id="cf-label" className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <Field label={t('customFields.code')} htmlFor="cf-code" required error={codeError} help={t('adm.fields.code.help')}>
          <input id="cf-code" className={codeError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(codeError)}
                 placeholder="budget_source" value={code} onChange={(e) => setCode(e.target.value)} />
          {codeError && suggestion && suggestion !== normalized && CODE.test(suggestion) && (
            <span className="row" style={{ gap: 8, marginTop: 2 }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCode(suggestion)}>
                <Icon name="check" size="sm" />{t('adm.fields.use')} <span className="mono">{suggestion}</span>
              </button>
              <span className="help">{t('adm.fields.useHelp')}</span>
            </span>
          )}
          {!codeError && normalized !== '' && (
            <span className="flag flag-success t-sm"><Icon name="check" />{t('adm.fields.codeFree')}</span>
          )}
        </Field>
        {!code && suggestion && CODE.test(suggestion) && (
          <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setCode(suggestion)}>
            {t('adm.fields.use')} <span className="mono">{suggestion}</span>
          </button>
        )}
        <div className="field">
          <span className="label" id={typeId}>{t('customFields.type')}</span>
          <div className="seg" role="group" aria-labelledby={typeId} style={{ alignSelf: 'flex-start' }}>
            {TYPES.map((type) => (
              <button key={type} type="button" aria-pressed={dataType === type} onClick={() => setDataType(type)}>
                <Icon name={TYPE_ICON[type]} size="sm" />{t(`customFields.type.${type}`)}
              </button>
            ))}
          </div>
        </div>
        {dataType === 'enum' && (
          <div className="field" style={{ gap: 6 }}>
            <span className="label">{t('adm.fields.options')} <span className="req" aria-hidden="true">*</span></span>
            {options.map((option, index) => (
              <div className="row" style={{ gap: 6 }} key={index}>
                <span className="ord">{index + 1}</span>
                <input className="input input-sm" style={{ flex: 1 }} aria-label={t('adm.fields.optionN', { n: index + 1 })} value={option}
                       onChange={(e) => setOptions(options.map((value, at) => (at === index ? e.target.value : value)))} />
                <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('adm.fields.removeOption', { n: index + 1 })}
                        disabled={options.length === 1} onClick={() => setOptions(options.filter((_, at) => at !== index))}>
                  <Icon name="close" size="sm" />
                </button>
              </div>
            ))}
            <div className="row" style={{ gap: 8, paddingLeft: 28 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOptions([...options, ''])}>
                <Icon name="plus" size="sm" />{t('adm.fields.addOption')}
              </button>
              <span className="t-xs muted">{t('adm.fields.optionsOrder')}</span>
            </div>
          </div>
        )}
        <div className="field">
          <Switch checked={required} onChange={setRequired}
                  label={`${t('customFields.required')} · ${required ? t('adm.on') : t('adm.off')}`} />
          <span className="help">{required ? t('adm.fields.required.on') : t('adm.fields.required.off')}</span>
        </div>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
